import { ref } from 'vue';
import { api } from '../api.js';

/**
 * Per-user sidebar stack ordering (`sidebarOrder` user setting).
 *
 * The persisted order is a preference, not a schema: it only ever needs to
 * hold names the user has actually seen and reordered. New stacks that show
 * up later (env spun up a new compose stack) aren't in it yet, so on every
 * render we merge the persisted order with whatever stacks currently exist —
 * known order is respected, unknown stacks are appended alphabetically, and
 * anything persisted that no longer exists is silently dropped.
 */
export function mergeSidebarOrder(persisted: string[], knownStacks: string[]): string[] {
  const known = new Set(knownStacks);
  const ordered = persisted.filter((s) => known.has(s));
  const seen = new Set(ordered);
  const rest = knownStacks.filter((s) => !seen.has(s)).sort((a, b) => a.localeCompare(b));
  return [...ordered, ...rest];
}

/**
 * Reactive wrapper around the `sidebarOrder` user setting: loads it once,
 * and persists reorders with an optimistic update (rolled back if the PUT
 * fails).
 */
export function useUserSettings() {
  const sidebarOrder = ref<string[]>([]);
  const loaded = ref(false);

  // Guards against a slow initial load landing after a user has already
  // reordered (and thus already has a newer optimistic value in place).
  let loadSeq = 0;

  async function loadSidebarOrder(): Promise<void> {
    const seq = ++loadSeq;
    try {
      const value = await api.getUserSetting<unknown>('sidebarOrder');
      if (seq !== loadSeq) return;
      sidebarOrder.value = Array.isArray(value) ? value.filter((v) => typeof v === 'string') : [];
    } catch {
      if (seq !== loadSeq) return;
      sidebarOrder.value = [];
    } finally {
      if (seq === loadSeq) loaded.value = true;
    }
  }

  async function setSidebarOrder(order: string[]): Promise<void> {
    // Bump loadSeq so a slower initial loadSidebarOrder() GET that's still in flight can't land
    // afterward and clobber this newer optimistic value -- mutations always supersede loads.
    loadSeq++;
    const previous = sidebarOrder.value;
    sidebarOrder.value = order; // optimistic
    try {
      await api.putUserSetting('sidebarOrder', order);
    } catch (e) {
      sidebarOrder.value = previous; // roll back on failure
      throw e;
    }
  }

  return { sidebarOrder, loaded, loadSidebarOrder, setSidebarOrder };
}
